import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    ReactNode,
    useCallback,
} from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
    type ApiKeyState,
    type ApiKeyProvider,
    type UserProfile as ApiUserProfile,
    getUserProfile,
    saveApiKey,
    updateUserProfile,
} from "@/app/lib/auditflowApi";

interface UserProfile {
    displayName: string | null;
    organisation: string | null;
    messageCreditsUsed: number;
    creditsResetDate: string;
    creditsRemaining: number;
    tier: string;
    tabularModel: string;
    chatModel: string;
    suggestionModel: string;
    suggestionPromptTemplate: string;
    apiKeys: ApiKeyState;
}

interface UserProfileContextType {
    profile: UserProfile | null;
    loading: boolean;
    updateDisplayName: (name: string) => Promise<boolean>;
    updateOrganisation: (organisation: string) => Promise<boolean>;
    updateModelPreference: (
        field: "tabularModel" | "chatModel" | "suggestionModel",
        value: string,
    ) => Promise<boolean>;
    updateSuggestionPromptTemplate: (template: string) => Promise<boolean>;
    updateApiKey: (
        provider: ApiKeyProvider,
        value: string | null,
    ) => Promise<boolean>;
    reloadProfile: () => Promise<void>;
    incrementMessageCredits: () => Promise<boolean>;
}

const UserProfileContext = createContext<UserProfileContextType | undefined>(
    undefined,
);

const API_KEY_PROVIDERS: ApiKeyProvider[] = [
    "claude",
    "gemini",
    "openai",
    "kimi",
    "openrouter",
];

function emptyApiKeys(): ApiKeyState {
    return {
        claude: { configured: false, source: null },
        gemini: { configured: false, source: null },
        openai: { configured: false, source: null },
        kimi: { configured: false, source: null },
        openrouter: { configured: false, source: null },
    };
}

function toProfile(
    data: ApiUserProfile,
    existingApiKeys?: ApiKeyState,
): UserProfile {
    const { apiKeyStatus, ...profile } = data;
    const apiKeys = existingApiKeys ?? emptyApiKeys();
    if (apiKeyStatus) {
        for (const provider of API_KEY_PROVIDERS) {
            apiKeys[provider] = {
                configured: !!apiKeyStatus[provider],
                source:
                    apiKeyStatus.sources?.[provider] ??
                    (apiKeyStatus[provider] ? "user" : null),
            };
        }
    }

    return {
        ...profile,
        apiKeys,
    };
}

export function UserProfileProvider({ children }: { children: ReactNode }) {
    const { user, isAuthenticated } = useAuth();
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(true);

    const loadProfile = useCallback(async () => {
        try {
            const profileData = await getUserProfile();
            setProfile(toProfile(profileData));
        } catch {
            // Calculate a default future reset date for fallback
            const futureResetDate = new Date();
            futureResetDate.setDate(futureResetDate.getDate() + 30);

            // Set fallback profile data on exception
            setProfile({
                displayName: null,
                organisation: null,
                messageCreditsUsed: 0,
                creditsResetDate: futureResetDate.toISOString(),
                creditsRemaining: 999999, // temporarily unlimited
                tier: "Free",
                tabularModel: "gemini-3-flash-preview",
                chatModel: "gemini-3-flash-preview",
                suggestionModel: "gemini-3-flash-preview",
                suggestionPromptTemplate:
                    'You are a legal document analysis assistant. Based on the document text provided, suggest {{num_questions}} concise, insightful questions a lawyer would ask about this document. Return ONLY valid JSON in this exact format: {"suggestions":["question 1","question 2","question 3","question 4"]}. No other text.',
                apiKeys: emptyApiKeys(),
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isAuthenticated && user) {
            setLoading(true);
            loadProfile();
        } else {
            setProfile(null);
            setLoading(false);
        }
    }, [isAuthenticated, user, loadProfile]);

    const updateDisplayName = useCallback(
        async (displayName: string): Promise<boolean> => {
            if (!user) {
                return false;
            }

            try {
                const updated = await updateUserProfile({ displayName });
                setProfile((prev) =>
                    prev ? { ...prev, ...toProfile(updated) } : null,
                );
                return true;
            } catch {
                return false;
            }
        },
        [user],
    );

    const updateOrganisation = useCallback(
        async (organisation: string): Promise<boolean> => {
            if (!user) return false;
            try {
                const updated = await updateUserProfile({ organisation });
                setProfile((prev) =>
                    prev ? { ...prev, ...toProfile(updated) } : null,
                );
                return true;
            } catch {
                return false;
            }
        },
        [user],
    );

    const updateModelPreference = useCallback(
        async (
            field: "tabularModel" | "chatModel" | "suggestionModel",
            value: string,
        ): Promise<boolean> => {
            if (!user) return false;
            const payload: Record<string, string> = {};
            if (field === "tabularModel") payload.tabularModel = value;
            else if (field === "chatModel") payload.chatModel = value;
            else if (field === "suggestionModel") payload.suggestionModel = value;
            else return false;

            let previousValue: string | undefined;
            setProfile((prev) => {
                if (!prev) return prev;
                previousValue = prev[field];
                return { ...prev, [field]: value };
            });

            try {
                const updated = await updateUserProfile(payload);
                setProfile((prev) =>
                    prev
                        ? {
                              ...prev,
                              ...toProfile(updated, prev.apiKeys),
                          }
                        : null,
                );
                return true;
            } catch {
                if (previousValue !== undefined) {
                    setProfile((prev) =>
                        prev ? { ...prev, [field]: previousValue! } : prev,
                    );
                }
                return false;
            }
        },
        [user],
    );

    const updateSuggestionPromptTemplate = useCallback(
        async (template: string): Promise<boolean> => {
            if (!user) return false;
            try {
                const updated = await updateUserProfile({
                    suggestionPromptTemplate: template,
                });
                setProfile((prev) =>
                    prev ? { ...prev, ...toProfile(updated) } : null,
                );
                return true;
            } catch {
                return false;
            }
        },
        [user],
    );

    const updateApiKey = useCallback(
        async (
            provider: ApiKeyProvider,
            value: string | null,
        ): Promise<boolean> => {
            if (!user) return false;
            const normalized = value?.trim() ? value.trim() : null;
            try {
                await saveApiKey(provider, normalized);
                setProfile((prev) =>
                    prev
                        ? {
                              ...prev,
                              apiKeys: {
                                  ...prev.apiKeys,
                                  [provider]: {
                                      configured: !!normalized,
                                      source: normalized ? "user" : null,
                                  },
                              },
                          }
                        : null,
                );
                return true;
            } catch {
                return false;
            }
        },
        [user],
    );

    const reloadProfile = useCallback(async () => {
        if (user) {
            await loadProfile();
        }
    }, [user, loadProfile]);

    const incrementMessageCredits = useCallback(async (): Promise<boolean> => {
        if (!user || !profile) {
            return false;
        }

        // Check if user has credits remaining
        if (profile.creditsRemaining <= 0) {
            return false;
        }

        return false;
    }, [user, profile]);

    return (
        <UserProfileContext.Provider
            value={{
                profile,
                loading,
                updateDisplayName,
                updateOrganisation,
                updateModelPreference,
                updateSuggestionPromptTemplate,
                updateApiKey,
                reloadProfile,
                incrementMessageCredits,
            }}
        >
            {children}
        </UserProfileContext.Provider>
    );
}

export function useUserProfile() {
    const context = useContext(UserProfileContext);
    if (context === undefined) {
        throw new Error(
            "useUserProfile must be used within a UserProfileProvider",
        );
    }
    return context;
}
